import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '@/lib/supabase';
import { buildMcpAuthorizeUrlWithHandoff } from '@/services/mcpOAuthHandoff';

/**
 * Global OAuth handler that checks for active OAuth flows and redirects authenticated users
 * This handles cases where users navigate away from the login page before OAuth redirect completes
 */
export function useGlobalOAuthHandler() {
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    const handleGlobalOAuth = async () => {
      // Only proceed if user is authenticated and not loading
      if (!isAuthenticated || isLoading) {
        return;
      }

      // Check for active OAuth flow in sessionStorage
      const sessionOAuthData = sessionStorage.getItem('mcp_oauth_flow_active');
      if (!sessionOAuthData) {
        return;
      }

      try {
        const oauthFlow = JSON.parse(sessionOAuthData);
        console.log('GlobalOAuth: Found active OAuth flow for authenticated user:', oauthFlow);

        // Validate OAuth parameters
        if (!oauthFlow.client_id || !oauthFlow.redirect_uri || !oauthFlow.response_type) {
          console.log('GlobalOAuth: Invalid OAuth flow data, clearing sessionStorage');
          sessionStorage.removeItem('mcp_oauth_flow_active');
          return;
        }

        // Get the current session token
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData.session;
        if (!session?.access_token || !session.refresh_token) {
          console.log('GlobalOAuth: No session tokens found, cannot redirect');
          return;
        }

        console.log('GlobalOAuth: Redirecting authenticated user to MCP server');

        const oauthParams: Record<string, string> = {};
        
        if (oauthFlow.client_id) oauthParams.client_id = oauthFlow.client_id;
        if (oauthFlow.redirect_uri) oauthParams.redirect_uri = oauthFlow.redirect_uri;
        if (oauthFlow.response_type) oauthParams.response_type = oauthFlow.response_type;
        if (oauthFlow.scope) oauthParams.scope = oauthFlow.scope;
        if (oauthFlow.state) oauthParams.state = oauthFlow.state;
        if (oauthFlow.code_challenge) oauthParams.code_challenge = oauthFlow.code_challenge;
        if (oauthFlow.code_challenge_method) oauthParams.code_challenge_method = oauthFlow.code_challenge_method;

        const mcpOAuthUrl = await buildMcpAuthorizeUrlWithHandoff(
          session.access_token,
          session.refresh_token,
          oauthParams
        );
        console.log('GlobalOAuth: Redirecting to MCP OAuth URL');

        sessionStorage.removeItem('mcp_oauth_flow_active');
        window.location.href = mcpOAuthUrl;

      } catch (error) {
        console.error('GlobalOAuth: Error processing OAuth flow:', error);
        // Clear corrupted data
        sessionStorage.removeItem('mcp_oauth_flow_active');
      }
    };

    // Add a small delay to ensure auth state is fully settled
    const timeoutId = setTimeout(handleGlobalOAuth, 100);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, isLoading]);
}
